import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import messagingService from '../../services/messagingService';
import { supabase } from '../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return date?.toLocaleDateString('fr-FR', { weekday: 'short' });
  return date?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const THREAD_TYPE_CONFIG = {
  appointment: { label: 'Rendez-vous', icon: 'CalendarClock', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  request: { label: 'Demande', icon: 'ClipboardList', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  question: { label: 'Question', icon: 'HelpCircle', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  transaction: { label: 'Transaction', icon: 'Receipt', color: 'bg-green-50 text-green-700 border-green-200' },
  general: { label: 'Général', icon: 'MessageSquare', color: 'bg-slate-50 text-slate-700 border-slate-200' },
};

const detectThreadType = (subject = '', messageType = '') => {
  const s = (subject + ' ' + messageType)?.toLowerCase();
  if (s?.includes('rendez') || s?.includes('rdv') || s?.includes('appointment') || s?.includes('booking')) return 'appointment';
  if (s?.includes('demande') || s?.includes('request') || s?.includes('devis')) return 'request';
  if (s?.includes('question') || s?.includes('info') || s?.includes('renseignement')) return 'question';
  if (s?.includes('transaction') || s?.includes('paiement') || s?.includes('facture') || s?.includes('commission')) return 'transaction';
  return 'general';
};

const getInitials = (name = '') =>
  name?.split(' ')?.slice(0, 2)?.map(w => w?.[0]?.toUpperCase())?.join('') || '?';

// ─── New Conversation Modal ───────────────────────────────────────────────────

const NewConversationModal = ({ onClose, onCreate, bookings }) => {
  const [subject, setSubject] = useState('');
  const [selectedBooking, setSelectedBooking] = useState('');
  const [threadType, setThreadType] = useState('general');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!subject?.trim()) return;
    setLoading(true);
    await onCreate({
      subject: subject?.trim(),
      bookingId: selectedBooking || null,
      clientName: clientName?.trim(),
      clientEmail: clientEmail?.trim(),
      threadType,
    });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
          <h2 className="text-ios-headline font-display font-display-semibold text-text-primary">Nouvelle conversation</h2>
          <button onClick={onClose} className="p-2 hover:bg-surface rounded-xl transition-colors">
            <Icon name="X" size={18} className="text-text-secondary" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Thread type */}
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-2">Type de conversation</label>
            <div className="grid grid-cols-5 gap-1.5">
              {Object.entries(THREAD_TYPE_CONFIG)?.map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setThreadType(key)}
                  className={`flex flex-col items-center p-2 rounded-xl border text-center transition-all duration-200 ${
                    threadType === key
                      ? `${cfg?.color} border-current`
                      : 'bg-surface border-border/40 text-text-secondary hover:border-border'
                  }`}
                >
                  <Icon name={cfg?.icon} size={14} />
                  <span className="text-ios-caption2 font-body-medium mt-1 leading-tight">{cfg?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Sujet *</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e?.target?.value)}
              placeholder="Ex: Question sur le rendez-vous du 15 juillet"
              className="w-full px-4 py-3 border border-border/60 rounded-xl focus:ring-2 focus:ring-brand-earth/30 focus:border-brand-earth text-ios-callout bg-white"
            />
          </div>

          {/* Client info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Nom du client</label>
              <input
                type="text"
                value={clientName}
                onChange={e => setClientName(e?.target?.value)}
                placeholder="Nom complet"
                className="w-full px-3 py-2.5 border border-border/60 rounded-xl focus:ring-2 focus:ring-brand-earth/30 focus:border-brand-earth text-ios-callout bg-white"
              />
            </div>
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Email client</label>
              <input
                type="email"
                value={clientEmail}
                onChange={e => setClientEmail(e?.target?.value)}
                placeholder="email@exemple.com"
                className="w-full px-3 py-2.5 border border-border/60 rounded-xl focus:ring-2 focus:ring-brand-earth/30 focus:border-brand-earth text-ios-callout bg-white"
              />
            </div>
          </div>

          {/* Link to booking */}
          {bookings?.length > 0 && (
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Lier à une réservation (optionnel)</label>
              <select
                value={selectedBooking}
                onChange={e => setSelectedBooking(e?.target?.value)}
                className="w-full px-4 py-3 border border-border/60 rounded-xl focus:ring-2 focus:ring-brand-earth/30 focus:border-brand-earth text-ios-callout bg-white"
              >
                <option value="">Aucune réservation</option>
                {bookings?.map(b => (
                  <option key={b?.id} value={b?.id}>
                    {b?.client_name} — {b?.service_type?.replace(/_/g, ' ')} · {b?.preferred_date || 'Date à confirmer'}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-border/60 rounded-xl text-ios-callout font-body-medium text-text-secondary hover:bg-surface transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleCreate}
            disabled={!subject?.trim() || loading}
            className="flex-1 px-4 py-3 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-ios-light"
          >
            {loading ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Plus" size={16} />}
            Créer
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Conversation List Item ───────────────────────────────────────────────────

const ConversationItem = ({ conv, isActive, onClick, currentUserId }) => {
  const lastMsg = conv?.messages?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))?.[0];
  const unreadCount = conv?.messages?.filter(m => !m?.is_read && m?.sender_id !== currentUserId)?.length || 0;
  const threadType = detectThreadType(conv?.subject, lastMsg?.message_type);
  const cfg = THREAD_TYPE_CONFIG?.[threadType];

  const otherParticipant = conv?.conversation_participants?.find(p => p?.user_id !== currentUserId);
  const displayName = otherParticipant?.participant_name || conv?.subject || 'Conversation';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3.5 border-b border-border/30 transition-all duration-200 hover:bg-surface/80 ${
        isActive ? 'bg-brand-earth/5 border-l-2 border-l-brand-earth' : ''
      }`}
    >
      <div className="flex items-start space-x-3">
        {/* Avatar */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-display font-display-semibold text-ios-callout ${
          isActive ? 'bg-brand-earth text-white' : 'bg-gradient-to-br from-brand-earth/20 to-brand-gold/20 text-brand-earth'
        }`}>
          {getInitials(displayName)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-0.5">
            <span className={`text-ios-callout font-body-semibold truncate ${unreadCount > 0 ? 'text-text-primary' : 'text-text-secondary'}`}>
              {displayName}
            </span>
            <div className="flex items-center space-x-1.5 flex-shrink-0 ml-2">
              {unreadCount > 0 && (
                <span className="w-5 h-5 bg-brand-earth text-white rounded-full text-ios-caption2 font-body-semibold flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
              <span className="text-ios-caption2 text-text-secondary">{formatTime(conv?.last_message_at)}</span>
            </div>
          </div>

          <div className="flex items-center space-x-2 mb-1">
            <span className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded-md text-ios-caption2 font-body-medium border ${cfg?.color}`}>
              <Icon name={cfg?.icon} size={10} />
              <span>{cfg?.label}</span>
            </span>
          </div>

          <p className={`text-ios-caption1 truncate ${unreadCount > 0 ? 'text-text-primary font-body-medium' : 'text-text-secondary'}`}>
            {lastMsg?.content || conv?.subject || 'Aucun message'}
          </p>
        </div>
      </div>
    </button>
  );
};

// ─── Message Bubble ───────────────────────────────────────────────────────────

const MessageBubble = ({ msg, isOwn }) => {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isOwn && (
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-earth/20 to-brand-gold/20 flex items-center justify-center flex-shrink-0 mr-2 mt-1">
          <span className="text-ios-caption2 font-body-semibold text-brand-earth">
            {getInitials(msg?.sender_name)}
          </span>
        </div>
      )}
      <div className={`max-w-[72%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
        {!isOwn && (
          <span className="text-ios-caption2 text-text-secondary mb-1 ml-1">{msg?.sender_name}</span>
        )}
        <div className={`px-4 py-3 rounded-2xl text-ios-callout leading-relaxed ${
          isOwn
            ? 'bg-brand-earth text-white rounded-tr-md' :'bg-white border border-border/50 text-text-primary rounded-tl-md shadow-ios-light'
        }`}>
          {msg?.content}
        </div>
        <span className={`text-ios-caption2 text-text-secondary mt-1 ${isOwn ? 'mr-1' : 'ml-1'}`}>
          {formatFullDate(msg?.created_at)}
        </span>
      </div>
    </div>
  );
};

// ─── Thread Detail Panel ──────────────────────────────────────────────────────

const ThreadDetail = ({ conv, currentUserId, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const threadType = detectThreadType(conv?.subject);
  const cfg = THREAD_TYPE_CONFIG?.[threadType];
  const otherParticipant = conv?.conversation_participants?.find(p => p?.user_id !== currentUserId);

  const loadMessages = useCallback(async () => {
    if (!conv?.id) return;
    setLoading(true);
    const { data } = await messagingService?.getMessages(conv?.id);
    setMessages(data || []);
    await messagingService?.markAsRead(conv?.id);
    setLoading(false);
  }, [conv?.id]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    bottomRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Real-time subscription
  useEffect(() => {
    if (!conv?.id) return;
    const channel = messagingService?.subscribeToMessages(conv?.id, (newMsg) => {
      setMessages(prev => {
        if (prev?.find(m => m?.id === newMsg?.id)) return prev;
        return [...prev, newMsg];
      });
      bottomRef?.current?.scrollIntoView({ behavior: 'smooth' });
    });
    return () => {
      if (channel) supabase?.removeChannel(channel);
    };
  }, [conv?.id]);

  const handleSend = async () => {
    if (!text?.trim() || sending) return;
    setSending(true);
    const { data } = await messagingService?.sendMessage({
      conversationId: conv?.id,
      content: text?.trim(),
    });
    if (data) {
      setMessages(prev => [...prev, data]);
      setText('');
      bottomRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
    setSending(false);
    inputRef?.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="flex items-center space-x-3 px-5 py-4 border-b border-border/40 bg-white flex-shrink-0">
        <button
          onClick={onBack}
          className="lg:hidden p-2 hover:bg-surface rounded-xl transition-colors mr-1"
        >
          <Icon name="ArrowLeft" size={18} className="text-text-secondary" />
        </button>

        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-earth/20 to-brand-gold/20 flex items-center justify-center flex-shrink-0">
          <span className="text-ios-callout font-display font-display-semibold text-brand-earth">
            {getInitials(otherParticipant?.participant_name || conv?.subject)}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-ios-callout font-body-semibold text-text-primary truncate">
              {otherParticipant?.participant_name || 'Client'}
            </h3>
            <span className={`flex-shrink-0 inline-flex items-center space-x-1 px-2 py-0.5 rounded-lg text-ios-caption2 font-body-medium border ${cfg?.color}`}>
              <Icon name={cfg?.icon} size={10} />
              <span>{cfg?.label}</span>
            </span>
          </div>
          <p className="text-ios-caption1 text-text-secondary truncate">{conv?.subject}</p>
        </div>

        {otherParticipant?.participant_email && (
          <a
            href={`mailto:${otherParticipant?.participant_email}`}
            className="p-2 hover:bg-surface rounded-xl transition-colors flex-shrink-0"
            title="Envoyer un email"
          >
            <Icon name="Mail" size={16} className="text-text-secondary" />
          </a>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-5 py-4 bg-surface/30">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-7 h-7 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-14 h-14 bg-white rounded-2xl shadow-ios-light flex items-center justify-center mb-3">
              <Icon name="MessageSquarePlus" size={24} className="text-text-secondary" />
            </div>
            <p className="text-ios-callout font-body-medium text-text-secondary">Aucun message</p>
            <p className="text-ios-caption1 text-text-secondary mt-1">Commencez la conversation ci-dessous.</p>
          </div>
        ) : (
          <>
            {messages?.map((msg) => (
              <MessageBubble
                key={msg?.id}
                msg={msg}
                isOwn={msg?.sender_id === currentUserId}
              />
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 px-4 py-3 bg-white border-t border-border/40">
        <div className="flex items-end space-x-3">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={text}
              onChange={e => setText(e?.target?.value)}
              onKeyDown={handleKeyDown}
              placeholder="Écrivez votre message… (Entrée pour envoyer)"
              rows={1}
              className="w-full px-4 py-3 border border-border/60 rounded-xl focus:ring-2 focus:ring-brand-earth/30 focus:border-brand-earth text-ios-callout bg-surface/50 resize-none leading-relaxed"
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!text?.trim() || sending}
            className="w-11 h-11 bg-brand-earth text-white rounded-xl flex items-center justify-center hover:bg-brand-earth/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-ios-light flex-shrink-0"
          >
            {sending
              ? <Icon name="Loader2" size={17} className="animate-spin" />
              : <Icon name="Send" size={17} />
            }
          </button>
        </div>
        <p className="text-ios-caption2 text-text-secondary mt-1.5 ml-1">Appuyez sur Entrée pour envoyer · Maj+Entrée pour une nouvelle ligne</p>
      </div>
    </div>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyInbox = () => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8">
    <div className="w-20 h-20 bg-gradient-to-br from-brand-earth/10 to-brand-gold/10 rounded-3xl flex items-center justify-center mb-5">
      <Icon name="Inbox" size={36} className="text-brand-earth" />
    </div>
    <h3 className="text-ios-title3 font-display font-display-semibold text-text-primary mb-2">
      Boîte de réception vide
    </h3>
    <p className="text-ios-callout text-text-secondary max-w-xs leading-relaxed">
      Vos échanges avec les clients apparaîtront ici — rendez-vous, demandes, questions et mises à jour de transactions.
    </p>
  </div>
);

// ─── Commission Summary Card ──────────────────────────────────────────────────
const CommissionSummaryCard = ({ userId }) => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCommissions = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await supabase
        ?.from('commission_payouts')
        ?.select(`
          *,
          escrow_transaction:escrow_transactions(id, reference, title, item_title, amount_total, total_amount, currency)
        `)
        ?.eq('recipient_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(10);
      setCommissions(data || []);
    } catch (_) {}
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchCommissions(); }, [fetchCommissions]);

  // Realtime subscription
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      ?.channel(`commissions_${userId}`)
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'commission_payouts', filter: `recipient_id=eq.${userId}` }, () => {
        fetchCommissions();
      })
      ?.subscribe();
    return () => { supabase?.removeChannel(channel); };
  }, [userId, fetchCommissions]);

  const totalPending = commissions?.filter(c => c?.status === 'pending')?.reduce((s, c) => s + (c?.commission_amount || 0), 0);
  const totalReleased = commissions?.filter(c => c?.status === 'released')?.reduce((s, c) => s + (c?.commission_amount || 0), 0);

  const formatAmount = (amount, currency = 'XOF') => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
  };

  if (loading) return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 p-5 animate-pulse">
      <div className="h-4 bg-border/40 rounded w-32 mb-3" />
      <div className="h-8 bg-border/30 rounded w-24" />
    </div>
  );

  return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 shadow-ios-light overflow-hidden">
      <div className="px-5 py-4 border-b border-border/40 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
          <Icon name="DollarSign" size={18} className="text-amber-600" />
        </div>
        <div>
          <p className="text-ios-callout font-semibold text-text-primary">Résumé des commissions</p>
          <p className="text-ios-caption1 text-text-secondary">Vos commissions sur les transactions</p>
        </div>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-amber-50 rounded-xl p-3 text-center">
            <p className="text-xs text-amber-600 font-medium mb-1">En attente</p>
            <p className="text-lg font-bold text-amber-700">{formatAmount(totalPending)}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <p className="text-xs text-emerald-600 font-medium mb-1">Libérées</p>
            <p className="text-lg font-bold text-emerald-700">{formatAmount(totalReleased)}</p>
          </div>
        </div>
        {commissions?.length === 0 ? (
          <p className="text-xs text-text-secondary text-center py-2">Aucune commission enregistrée</p>
        ) : (
          <div className="space-y-2">
            {commissions?.slice(0, 4)?.map(c => {
              const txRef = c?.escrow_transaction?.reference || c?.escrow_transaction_id?.slice(0, 8);
              const txTitle = c?.escrow_transaction?.title || c?.escrow_transaction?.item_title;
              return (
                <div key={c?.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-text-primary truncate">{txTitle || `Réf: ${txRef}`}</p>
                    <p className="text-[10px] text-text-secondary">{c?.commission_rate}% · {new Date(c?.created_at)?.toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className="text-xs font-bold text-amber-700">{formatAmount(c?.commission_amount, c?.currency)}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${c?.status === 'released' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {c?.status === 'released' ? 'Libérée' : 'En attente'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Payment Notifications Panel ─────────────────────────────────────────────
const PaymentNotificationsPanel = ({ userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.in('type', ['escrow_released', 'commission_released', 'payment_received', 'escrow_disputed', 'escrow_resolved', 'escrow_refunded', 'commission_payout'])
        ?.order('created_at', { ascending: false })
        ?.limit(15);
      setNotifications(data || []);
    } catch (_) {}
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Realtime
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      ?.channel(`payment_notifs_${userId}`)
      ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` }, () => {
        fetchNotifications();
      })
      ?.subscribe();
    return () => { supabase?.removeChannel(channel); };
  }, [userId, fetchNotifications]);

  const NOTIF_CONFIG = {
    escrow_released: { icon: 'Unlock', color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Fonds libérés' },
    commission_released: { icon: 'DollarSign', color: 'text-amber-600', bg: 'bg-amber-50', label: 'Commission libérée' },
    payment_received: { icon: 'CreditCard', color: 'text-blue-600', bg: 'bg-blue-50', label: 'Paiement reçu' },
    escrow_disputed: { icon: 'AlertTriangle', color: 'text-red-600', bg: 'bg-red-50', label: 'Litige ouvert' },
    escrow_resolved: { icon: 'CheckCircle', color: 'text-blue-600', bg: 'bg-blue-50', label: 'Litige résolu' },
    escrow_refunded: { icon: 'RotateCcw', color: 'text-purple-600', bg: 'bg-purple-50', label: 'Remboursement' },
    commission_payout: { icon: 'DollarSign', color: 'text-amber-600', bg: 'bg-amber-50', label: 'Commission créée' },
  };

  if (loading) return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 p-5 animate-pulse">
      <div className="h-4 bg-border/40 rounded w-40 mb-3" />
      {[1,2,3]?.map(i => <div key={i} className="h-12 bg-border/20 rounded-xl mb-2" />)}
    </div>
  );

  return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 shadow-ios-light overflow-hidden">
      <div className="px-5 py-4 border-b border-border/40 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
          <Icon name="Bell" size={18} className="text-blue-600" />
        </div>
        <div className="flex-1">
          <p className="text-ios-callout font-semibold text-text-primary">Notifications de paiement</p>
          <p className="text-ios-caption1 text-text-secondary">Mises à jour en temps réel</p>
        </div>
        {notifications?.filter(n => !n?.is_read)?.length > 0 && (
          <span className="px-2 py-0.5 bg-brand-earth text-white text-[11px] font-bold rounded-full">
            {notifications?.filter(n => !n?.is_read)?.length}
          </span>
        )}
      </div>
      <div className="p-5">
        {notifications?.length === 0 ? (
          <div className="text-center py-4">
            <Icon name="Bell" size={28} className="text-border mx-auto mb-2" />
            <p className="text-xs text-text-secondary">Aucune notification de paiement</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications?.map(notif => {
              const cfg = NOTIF_CONFIG?.[notif?.type] || { icon: 'Bell', color: 'text-gray-500', bg: 'bg-gray-50', label: notif?.type };
              return (
                <div key={notif?.id} className={`flex items-start gap-3 p-3 rounded-xl ${notif?.is_read ? 'bg-surface/40' : 'bg-brand-earth/5 border border-brand-earth/10'}`}>
                  <div className={`w-8 h-8 rounded-xl ${cfg?.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={cfg?.icon} size={14} className={cfg?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-text-primary">{notif?.title || cfg?.label}</p>
                    {notif?.message && <p className="text-[11px] text-text-secondary mt-0.5 line-clamp-2">{notif?.message}</p>}
                    <p className="text-[10px] text-text-secondary mt-1">{new Date(notif?.created_at)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  {!notif?.is_read && <div className="w-2 h-2 rounded-full bg-brand-earth flex-shrink-0 mt-1" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ProfessionalInbox = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showMobileThread, setShowMobileThread] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);

  const activeConv = conversations?.find(c => c?.id === activeConvId) || null;

  const loadConversations = useCallback(async () => {
    setLoading(true);
    const { data } = await messagingService?.getConversations();
    setConversations(data || []);
    const { count } = await messagingService?.getUnreadCount();
    setUnreadTotal(count || 0);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    loadConversations();
  }, [user?.id, loadConversations]);

  // Load professional bookings for linking
  useEffect(() => {
    if (!user?.id) return;
    const fetchBookings = async () => {
      const { data } = await supabase
        ?.from('professional_bookings')
        ?.select('id, client_name, service_type, preferred_date, booking_status')
        ?.eq('booking_status', 'confirmed')
        ?.order('preferred_date', { ascending: false })
        ?.limit(20);
      setBookings(data || []);
    };
    fetchBookings();
  }, [user?.id]);

  const handleCreateConversation = async ({ subject, bookingId, clientName, clientEmail, threadType }) => {
    const { data } = await messagingService?.createConversation({
      bookingId,
      subject: `[${THREAD_TYPE_CONFIG?.[threadType]?.label}] ${subject}`,
      participantName: clientName || 'Client',
      participantEmail: clientEmail || '',
    });
    if (data) {
      await loadConversations();
      setActiveConvId(data?.id);
      setShowMobileThread(true);
    }
    setShowNewModal(false);
  };

  const handleSelectConv = (id) => {
    setActiveConvId(id);
    setShowMobileThread(true);
  };

  // Filter conversations
  const filteredConvs = conversations?.filter(conv => {
    const threadType = detectThreadType(conv?.subject);
    if (filter !== 'all' && threadType !== filter) return false;
    if (search?.trim()) {
      const q = search?.toLowerCase();
      const matchSubject = conv?.subject?.toLowerCase()?.includes(q);
      const matchParticipant = conv?.conversation_participants?.some(p =>
        p?.participant_name?.toLowerCase()?.includes(q) || p?.participant_email?.toLowerCase()?.includes(q)
      );
      if (!matchSubject && !matchParticipant) return false;
    }
    return true;
  });

  const unreadConvs = conversations?.filter(c =>
    c?.messages?.some(m => !m?.is_read && m?.sender_id !== user?.id)
  )?.length;

  if (!user) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 bg-brand-earth/10 rounded-2xl flex items-center justify-center mb-4">
            <Icon name="Lock" size={28} className="text-brand-earth" />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-text-primary mb-2">Connexion requise</h2>
          <p className="text-ios-callout text-text-secondary text-center mb-6 max-w-sm">
            Connectez-vous pour accéder à votre boîte de réception professionnelle.
          </p>
          <Link to="/auth" className="px-6 py-3 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors shadow-ios-light">
            Se connecter
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <Helmet>
        <title>Boîte de réception — Espace Professionnel · Kapital States</title>
        <meta name="description" content="Communiquez avec vos clients sur les rendez-vous, demandes, questions et mises à jour de transactions." />
      </Helmet>
      <Header />

      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Page header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-4">
            <div className="w-11 h-11 bg-gradient-to-br from-brand-gold to-brand-earth rounded-2xl flex items-center justify-center shadow-ios-light">
              <Icon name="Inbox" size={20} color="white" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-ios-title2 font-display font-display-semibold text-text-primary leading-tight">
                  Boîte de réception
                </h1>
                {unreadTotal > 0 && (
                  <span className="px-2 py-0.5 bg-brand-earth text-white rounded-full text-ios-caption2 font-body-semibold">
                    {unreadTotal} non lu{unreadTotal > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <p className="text-ios-caption1 text-text-secondary mt-0.5">
                Échanges clients · rendez-vous, demandes, questions, transactions
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Link
              to="/professional-portal"
              className="hidden sm:flex items-center space-x-1.5 px-3 py-2 border border-border/60 text-text-secondary rounded-xl text-ios-caption1 font-body-medium hover:bg-surface transition-colors"
            >
              <Icon name="ArrowLeft" size={14} />
              <span>Espace Pro</span>
            </Link>
            <button
              onClick={() => setShowNewModal(true)}
              className="flex items-center space-x-2 px-4 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-all shadow-ios-light"
            >
              <Icon name="Plus" size={16} />
              <span className="hidden sm:inline">Nouvelle conversation</span>
              <span className="sm:hidden">Nouveau</span>
            </button>
          </div>
        </div>

        {/* Main inbox layout */}
        <div className="flex-1 flex flex-col lg:flex-row gap-5">
          {/* Inbox panel */}
          <div className="flex-1 bg-white rounded-2xl shadow-ios-light border border-border/50 overflow-hidden flex" style={{ minHeight: '600px' }}>
            {/* Left panel — conversation list */}
            <div className={`w-full lg:w-80 xl:w-96 flex-shrink-0 flex flex-col border-r border-border/40 ${showMobileThread ? 'hidden lg:flex' : 'flex'}`}>
              {/* Search + filters */}
              <div className="p-3 border-b border-border/30 space-y-2.5">
                <div className="relative">
                  <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e?.target?.value)}
                    placeholder="Rechercher un client ou sujet…"
                    className="w-full pl-9 pr-4 py-2.5 bg-surface border border-border/40 rounded-xl text-ios-callout focus:ring-2 focus:ring-brand-earth/30 focus:border-brand-earth"
                  />
                </div>
                {/* Type filter pills */}
                <div className="flex items-center space-x-1.5 overflow-x-auto pb-0.5 scrollbar-hide">
                  {[
                    { k: 'all', l: 'Tous', icon: 'LayoutGrid' },
                    ...Object.entries(THREAD_TYPE_CONFIG)?.map(([k, v]) => ({ k, l: v?.label, icon: v?.icon })),
                  ]?.map(f => (
                    <button
                      key={f?.k}
                      onClick={() => setFilter(f?.k)}
                      className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-lg text-ios-caption1 font-body-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                        filter === f?.k
                          ? 'bg-brand-earth text-white' :'bg-surface border border-border/40 text-text-secondary hover:border-border'
                      }`}
                    >
                      <Icon name={f?.icon} size={11} />
                      <span>{f?.l}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Stats bar */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-surface/50 border-b border-border/30">
                <span className="text-ios-caption1 text-text-secondary">
                  {filteredConvs?.length} conversation{filteredConvs?.length !== 1 ? 's' : ''}
                </span>
                {unreadConvs > 0 && (
                  <span className="text-ios-caption1 text-brand-earth font-body-medium">
                    {unreadConvs} non lu{unreadConvs > 1 ? 'es' : 'e'}
                  </span>
                )}
              </div>

              {/* Conversation list */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-7 h-7 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredConvs?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="w-12 h-12 bg-surface rounded-2xl flex items-center justify-center mb-3">
                      <Icon name="MessageSquareOff" size={22} className="text-text-secondary" />
                    </div>
                    <p className="text-ios-callout font-body-medium text-text-secondary">
                      {search ? 'Aucun résultat' : 'Aucune conversation'}
                    </p>
                    {!search && (
                      <p className="text-ios-caption1 text-text-secondary mt-1">
                        Créez une nouvelle conversation pour commencer.
                      </p>
                    )}
                  </div>
                ) : (
                  filteredConvs?.map(conv => (
                    <ConversationItem
                      key={conv?.id}
                      conv={conv}
                      isActive={conv?.id === activeConvId}
                      onClick={() => handleSelectConv(conv?.id)}
                      currentUserId={user?.id}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Right panel — thread detail */}
            <div className={`flex-1 flex flex-col ${showMobileThread ? 'flex' : 'hidden lg:flex'}`}>
              {activeConv ? (
                <ThreadDetail
                  conv={activeConv}
                  currentUserId={user?.id}
                  onBack={() => setShowMobileThread(false)}
                />
              ) : (
                <EmptyInbox />
              )}
            </div>
          </div>

          {/* Right sidebar — payment notifications + commission summary */}
          <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 space-y-4">
            <PaymentNotificationsPanel userId={user?.id} />
            <CommissionSummaryCard userId={user?.id} />
          </div>
        </div>
      </div>

      {/* New conversation modal */}
      {showNewModal && (
        <NewConversationModal
          onClose={() => setShowNewModal(false)}
          onCreate={handleCreateConversation}
          bookings={bookings}
        />
      )}
    </div>
  );
};

export default ProfessionalInbox;
